import axios, { type AxiosResponse } from 'axios'

import {
  newsletterSignUpEventName,
  triggerGoogleTagManagerEvent,
} from './analytics'
import { type Locale } from './language'

type Service = 'klaviyo'

export interface KlaviyoNewsletterJoinPayload {
  listId: string
  name?: string
  email: string
}

export interface KlaviyoNewsletterJoinResponse {
  error?: string
}

export interface KlaviyoWaitlistJoinPayload {
  variantId: number
  email: string
  name?: string
}

export interface KlaviyoWaitlistJoinResponse {
  error?: string
}

type NewsletterJoinResponse = KlaviyoNewsletterJoinResponse

type WaitlistJoinResponse = KlaviyoWaitlistJoinResponse

const getApiUrl = (service: string, action: string) =>
  `/api/${service.toLowerCase()}/${action}`

const getApiHeaders = (locale: Locale): Record<string, any> => ({
  'Content-Type': 'application/json',
  'X-Locale': locale,
})

const callApi = async <T>(
  url: string,
  payload: any,
  headers: Record<string, any>
) =>
  await axios.post<T, AxiosResponse<T>, string>(url, JSON.stringify(payload), {
    headers,
  })

/**
 * Calls newsletter signup API page.
 */
export const addEmailToNewsletterList = async (
  locale: Locale,
  service: Service,
  newsletterTargetId: string,
  hasAcceptedTerms: boolean,
  email: string,
  name?: string
) => {
  const url = getApiUrl(service, 'newsletter-join')
  const payload =
    service === 'klaviyo'
      ? ({
          listId: newsletterTargetId,
          email,
          name,
        } satisfies KlaviyoNewsletterJoinPayload)
      : null
  const headers = getApiHeaders(locale)
  await callApi<NewsletterJoinResponse>(url, payload, headers)

  triggerGoogleTagManagerEvent(newsletterSignUpEventName)
}

/**
 * Calls waitlist signup API page.
 */
export const addEmailToWaitlist = async (
  locale: Locale,
  service: Service,
  variantId: number,
  email: string,
  name?: string
) => {
  const url = getApiUrl(service, 'waitlist-join')
  const payload =
    service === 'klaviyo'
      ? ({
          variantId,
          email,
          name,
        } satisfies KlaviyoWaitlistJoinPayload)
      : null
  const headers = getApiHeaders(locale)
  await callApi<WaitlistJoinResponse>(url, payload, headers)
}
